<template>
  <div class="history-conversation">
    <!-- 如果没有历史数据，不渲染任何内容 -->
    <template v-if="hasHistory">
      <!-- 如果未展开，显示点击加载提示 -->
      <div v-if="!showHistory" class="load-history-tip" @click="loadHistory">
        点击加载历史对话
      </div>
      <!-- 如果已展开，显示历史对话内容 -->
      <div v-else class="history-content">
        <div v-if="hasMore" class="history-content-more" @click="loadMore">
          加载更多
        </div>
        <ChatBody :messages="historyMessages" />
        <div ref="historyDivider" class="history-divider">以上为历史对话</div>
      </div>
    </template>
  </div>
</template>

<script>
import ChatBody from "@/components/ChatBody/index.vue";
import { getHistoricalMessages } from "@/api/chatApi.js";
import { getHistoryConversationId, clearHistoryConversationId } from "@/utils/storage.js";

export default {
  name: "HistoryConversation",
  components: {
    ChatBody,
  },
  data() {
    // 从本地缓存读取历史会话id（自动判断过期）
    const historyConversationId = getHistoryConversationId();
    return {
      historyConversationId,
      historyMessages: [],
      hasHistory: false,
      showHistory: false,
      isLoading: false,
      hasMore: false,
      firstId: null,
    };
  },
  async mounted() {
    await this.fetchHistoricalMessages();
  },
  methods: {
    /**
     * 获取历史消息
     * @param {boolean} keepScrollPosition - 是否保持滚动位置（加载更多时为 true）
     */
    async fetchHistoricalMessages(keepScrollPosition = false) {
      if (!this.historyConversationId) {
        return;
      }

      // 防止重复加载
      if (this.isLoading) {
        return;
      }

      this.isLoading = true;

      // 如果需要保持滚动位置，记录加载前的滚动高度
      let scrollContainer = null;
      let oldScrollHeight = 0;
      let oldScrollTop = 0;

      if (keepScrollPosition) {
        scrollContainer = this.$parent.$refs.scrollContainer;
        if (scrollContainer) {
          oldScrollHeight = scrollContainer.scrollHeight;
          oldScrollTop = scrollContainer.scrollTop;
        }
      }

      try {
        const {
          data: { data, hasMore },
        } = await getHistoricalMessages(
          this.historyConversationId,
          this.firstId
        );

        // 转换数据格式
        const messages = this.transformHistoryData(data);
        if (messages && messages.length > 0) {
          this.historyMessages = [...messages, ...this.historyMessages];
          this.hasHistory = true;
          this.hasMore = hasMore;

          // 如果需要保持滚动位置，在 DOM 更新后调整滚动
          if (keepScrollPosition && scrollContainer) {
            this.$nextTick(() => {
              // 计算新增内容的高度
              const newScrollHeight = scrollContainer.scrollHeight;
              const addedHeight = newScrollHeight - oldScrollHeight;

              // 调整滚动位置：原位置 + 新增高度
              scrollContainer.scrollTop = oldScrollTop + addedHeight;
            });
          }
        }
      } catch (error) {
        console.error("获取历史消息失败", error);
        // 接口报错时清空过期或无效的会话 ID 缓存
        clearHistoryConversationId();
        this.historyConversationId = "";
        // 同步清空 App.vue 中 sseHandler mixin 的 conversation_id，
        // 避免不刷新页面直接发消息时仍携带已过期的会话 ID
        if (this.$parent) {
          this.$parent.conversation_id = "";
        }
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * 转换历史消息数据格式
     * 将接口返回的数据转换为 ChatBody 组件需要的格式
     * @param {any} data - 接口返回的原始数据
     * @returns {Array} 转换后的消息数组
     */
    transformHistoryData(data) {
      if (!data?.length) {
        return [];
      }
      const messages = [];
      data.forEach((item, index) => {
        const { query, answer, createdAt, id } = item;
        if (index === 0) {
          this.firstId = id;
        }
        messages.push({
          content: query,
          time: new Date(createdAt * 1000).toISOString(),
          sender: "me",
          avatar: "https://via.placeholder.com/40",
        });
        messages.push({
          id,
          content: answer,
          time: new Date(createdAt * 1000).toISOString(),
          sender: "other",
          avatar: "/static/ai-avatar.png",
        });
      });
      return messages;
    },

    /**
     * 加载更多历史消息
     */
    loadMore() {
      // 传入 true 表示需要保持滚动位置
      this.fetchHistoricalMessages(true);
    },

    /**
     * 点击加载历史对话
     */
    loadHistory() {
      this.showHistory = true;

      // 等待 DOM 更新后滚动到历史对话分隔线位置
      this.$nextTick(() => {
        this.scrollToHistoryDivider();
      });
    },

    /**
     * 滚动到历史对话分隔线位置
     */
    scrollToHistoryDivider() {
      // 通过 $parent 访问父组件 App.vue 的 scrollContainer
      const scrollContainer = this.$parent.$refs.scrollContainer;
      const divider = this.$refs.historyDivider;

      if (scrollContainer && divider) {
        // 获取元素位置信息
        const containerRect = scrollContainer.getBoundingClientRect();
        const dividerRect = divider.getBoundingClientRect();

        // 计算分隔线相对于容器顶部的偏移量
        const relativeTop =
          dividerRect.top - containerRect.top + scrollContainer.scrollTop;

        // 滚动到分隔线位置，预留更多空间避免被输入框挡住（留120px边距）
        const targetScrollTop =
          relativeTop -
          scrollContainer.clientHeight +
          divider.offsetHeight +
          120;

        // 直接设置 scrollTop，无动画效果
        scrollContainer.scrollTop = targetScrollTop;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.history-conversation {
  width: 100%;
  box-sizing: border-box;
  min-height: 30px;

  .load-history-tip {
    width: 100%;
    padding: 16px;
    box-sizing: border-box;
    text-align: center;
    font-size: 12px;
    color: #888bcd;
  }

  .history-content {
    width: 100%;
    box-sizing: border-box;
    .history-content-more {
      width: 100%;
      padding-top: 12px;
      text-align: center;
      font-size: 12px;
      color: #888bcd;
    }

    .history-divider {
      width: 100%;
      padding: 16px;
      text-align: center;
      color: #999999;
      font-size: 12px;
      border-top: 1px solid #e5e5e5;
      margin-top: 8px;
      box-sizing: border-box;
    }
  }
}
</style>

